import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BSL_LEVELS } from '../../constants/sample.constants';
import type { ReportFilters } from '../../services/report.service';
import NotesFilterFields from './NotesFilterFields';

export type ReportDetailedFilterFieldsProps = {
  /** DOM id 接頭辞（reports / usage-records 等） */
  idPrefix: string;
  filters: ReportFilters;
  onFiltersChange: (next: ReportFilters) => void;
  isAdmin: boolean;
  /** 全保管庫表示時のみ作製者フィルターを出す（スコープ外では無効） */
  showCreatorFilter?: boolean;
  /** レポート向けの属・種・亜種・株 */
  showPathogenFields?: boolean;
  size?: 'sm';
};

function patch(
  filters: ReportFilters,
  key: keyof ReportFilters,
  value: string | boolean | undefined
): ReportFilters {
  return { ...filters, [key]: value === '' || value === undefined ? undefined : value };
}

function hasPathogenFilter(filters: ReportFilters): boolean {
  return Boolean(
    filters.pathogenGenus ||
      filters.pathogenSpecies ||
      filters.pathogenSubspecies ||
      filters.pathogenStrain
  );
}

/** レポート／使用履歴で共有する詳細フィルター項目 */
export default function ReportDetailedFilterFields({
  idPrefix,
  filters,
  onFiltersChange,
  isAdmin,
  showCreatorFilter,
  showPathogenFields = false,
  size,
}: ReportDetailedFilterFieldsProps) {
  const { t } = useTranslation();
  const labelClass = size === 'sm' ? 'small mb-0' : undefined;
  const creatorVisible = showCreatorFilter ?? isAdmin;
  const adminCol = creatorVisible ? 2 : 3;
  const pathogenOpen = hasPathogenFilter(filters);

  const textField = (
    key: keyof ReportFilters,
    labelKey: string,
    placeholderKey: string,
    md: number,
    fieldName: string
  ) => {
    const id = `${idPrefix}-filter-${fieldName}`;
    return (
      <Col xs={6} md={md} key={id}>
        <Form.Label htmlFor={id} className={labelClass}>
          {t(labelKey)}
        </Form.Label>
        <Form.Control
          id={id}
          name={fieldName}
          type="text"
          size={size}
          placeholder={t(placeholderKey)}
          value={(filters[key] as string | undefined) || ''}
          onChange={(e) => onFiltersChange(patch(filters, key, e.target.value))}
          autoComplete="off"
        />
      </Col>
    );
  };

  return (
    <div className="report-detailed-filter-fields">
      <Row className="g-2 mb-2">
        {creatorVisible &&
          textField('creator', 'reports.creator', 'reports.filter_creator_placeholder', 2, 'creator')}
        {textField(
          'location',
          'reports.location',
          'reports.filter_location_placeholder',
          adminCol,
          'location'
        )}
        {textField(
          'storageRoom',
          'reports.storage_room',
          'reports.filter_storage_room_placeholder',
          adminCol,
          'storage-room'
        )}
        {textField(
          'drawerNumber',
          'reports.drawer',
          'reports.filter_drawer_placeholder',
          2,
          'drawer'
        )}
        {textField('caseName', 'reports.case', 'reports.filter_case_placeholder', 2, 'case')}
        <Col xs={6} md={2}>
          <Form.Label htmlFor={`${idPrefix}-filter-bsl`} className={labelClass}>
            {t('reports.bsl_level')}
          </Form.Label>
          <Form.Select
            id={`${idPrefix}-filter-bsl`}
            name="filterBsl"
            size={size}
            value={filters.bslLevel || ''}
            onChange={(e) => onFiltersChange(patch(filters, 'bslLevel', e.target.value))}
          >
            <option value="">{t('reports.filter_all')}</option>
            {BSL_LEVELS.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>
      <Row className="g-2 mb-0">
        {textField(
          'sampleType',
          'sample.sample_type',
          'reports.filter_sample_type_placeholder',
          2,
          'type'
        )}
        {textField(
          'sampleName',
          'sample.sample_name',
          'reports.filter_sample_name_placeholder',
          2,
          'sample-name'
        )}
        <Col xs={12} md={2}>
          <NotesFilterFields
            notesId={`${idPrefix}-filter-notes`}
            notesEmptyId={`${idPrefix}-filter-notes-empty`}
            notesValue={filters.notes || ''}
            notesIsEmpty={filters.notesIsEmpty === true}
            notesLabelKey="sample.notes"
            notesPlaceholderKey="reports.filter_notes_placeholder"
            onNotesChange={(notes) =>
              onFiltersChange({
                ...filters,
                notes: notes || undefined,
                ...(notes ? { notesIsEmpty: undefined } : {}),
              })
            }
            onNotesIsEmptyChange={(isEmpty) =>
              onFiltersChange({
                ...filters,
                notesIsEmpty: isEmpty || undefined,
                ...(isEmpty ? { notes: undefined } : {}),
              })
            }
          />
        </Col>
      </Row>
      {showPathogenFields ? (
        <details
          className="report-pathogen-filters mt-2"
          ref={(el) => {
            if (el && pathogenOpen) el.open = true;
          }}
        >
          <summary
            className="small text-muted user-select-none"
            title={t('reports.pathogen_filters_toggle_hint', {
              defaultValue: 'Show pathogen genus, species, subspecies, and strain filters',
            })}
          >
            {t('reports.pathogen_filters_toggle', {
              defaultValue: 'Pathogen filters (genus / species / …)',
            })}
          </summary>
          <Row className="g-2 mt-1">
            {textField(
              'pathogenGenus',
              'reports.pathogen_genus',
              'reports.filter_pathogen_genus_placeholder',
              3,
              'pathogen-genus'
            )}
            {textField(
              'pathogenSpecies',
              'reports.pathogen_species',
              'reports.filter_pathogen_species_placeholder',
              3,
              'pathogen-species'
            )}
            {textField(
              'pathogenSubspecies',
              'reports.pathogen_subspecies',
              'reports.filter_pathogen_subspecies_placeholder',
              3,
              'pathogen-subspecies'
            )}
            {textField(
              'pathogenStrain',
              'reports.pathogen_strain',
              'reports.filter_pathogen_strain_placeholder',
              3,
              'pathogen-strain'
            )}
          </Row>
        </details>
      ) : null}
    </div>
  );
}
